import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getBannerService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';
import type {
  Banner,
  BannerCreateRequest,
  BannerUpdateRequest,
  BannerQueryParams,
  BannersData,
} from '../types/api/banner';

/**
 * Hook for fetching banners (for DataTable)
 */
export function useBanners() {
  const bannerService = getBannerService();

  const fetchBanners: FetchDataFn<Banner> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Banner>> => {
      const apiParams: BannerQueryParams = {
        page: params.page,
        size: params.pageSize,
      };

      // Extract isActive filter if present
      if (params.filters?.isActive?.value !== undefined && params.filters.isActive.value !== '') {
        const isActiveValue = params.filters.isActive.value;
        // Convert string to boolean if needed
        if (typeof isActiveValue === 'string') {
          apiParams.isActive = isActiveValue === 'true';
        } else {
          apiParams.isActive = Boolean(isActiveValue);
        }
      }

      const response: BannersData = await bannerService.getBanners(apiParams);

      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [bannerService]
  );

  return { fetchBanners };
}

/**
 * Hook for banner CRUD actions
 */
export function useBannerActions() {
  const bannerService = getBannerService();

  const createBannerWithImage = useCallback(
    async (payload: BannerCreateRequest, imageFile: File): Promise<Banner> => {
      try {
        const banner = await bannerService.createBannerWithImage(payload, imageFile);
        toast.success('Banner created successfully');
        return banner;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create banner');
        throw error;
      }
    },
    [bannerService]
  );

  const createBannerWithJson = useCallback(
    async (payload: BannerCreateRequest): Promise<Banner> => {
      try {
        const banner = await bannerService.createBannerWithJson(payload);
        toast.success('Banner created successfully');
        return banner;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create banner');
        throw error;
      }
    },
    [bannerService]
  );

  const getBanner = useCallback(
    async (bannerId: string): Promise<Banner> => {
      try {
        return await bannerService.getBanner(bannerId);
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch banner');
        throw error;
      }
    },
    [bannerService]
  );

  const updateBannerWithImage = useCallback(
    async (
      bannerId: string,
      payload: BannerUpdateRequest,
      imageFile?: File
    ): Promise<Banner> => {
      try {
        const banner = await bannerService.updateBannerWithImage(
          bannerId,
          payload,
          imageFile
        );
        toast.success('Banner updated successfully');
        return banner;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update banner');
        throw error;
      }
    },
    [bannerService]
  );

  const updateBannerWithJson = useCallback(
    async (bannerId: string, payload: BannerUpdateRequest): Promise<Banner> => {
      try {
        const banner = await bannerService.updateBannerWithJson(bannerId, payload);
        toast.success('Banner updated successfully');
        return banner;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update banner');
        throw error;
      }
    },
    [bannerService]
  );

  const deleteBanner = useCallback(
    async (bannerId: string): Promise<void> => {
      try {
        await bannerService.deleteBanner(bannerId);
        toast.success('Banner deleted successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete banner');
        throw error;
      }
    },
    [bannerService]
  );

  const activateBanner = useCallback(
    async (bannerId: string): Promise<void> => {
      try {
        await bannerService.activateBanner(bannerId);
        toast.success('Banner activated successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to activate banner');
        throw error;
      }
    },
    [bannerService]
  );

  const deactivateBanner = useCallback(
    async (bannerId: string): Promise<void> => {
      try {
        await bannerService.deactivateBanner(bannerId);
        toast.success('Banner deactivated successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to deactivate banner');
        throw error;
      }
    },
    [bannerService]
  );

  return {
    createBannerWithImage,
    createBannerWithJson,
    getBanner,
    updateBannerWithImage,
    updateBannerWithJson,
    deleteBanner,
    activateBanner,
    deactivateBanner,
  };
}

